import type { FC } from 'react';
import { cn } from '../../lib/utils';

interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  'aria-label'?: string;
  id?: string;
}

/**
 * Accessible pill-style toggle switch. Used in place of native checkboxes
 * for settings flags where the visual emphasis is "on / off" rather than
 * "selected among many".
 */
export const Switch: FC<SwitchProps> = ({ checked, onChange, disabled, id, ...rest }) => (
  <button
    type="button"
    role="switch"
    id={id}
    aria-checked={checked}
    aria-label={rest['aria-label']}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={cn(
      'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
      'disabled:cursor-not-allowed disabled:opacity-50',
      checked ? 'bg-primary' : 'bg-muted border border-border',
    )}
  >
    <span
      aria-hidden="true"
      className={cn(
        'pointer-events-none inline-block h-5 w-5 rounded-full bg-background shadow transform transition-transform',
        checked ? 'translate-x-5' : 'translate-x-0.5',
      )}
    />
  </button>
);
